/* custom-cl-cart-drawer.js — Cellova native cart drawer.
 *
 * HOOK MODEL (least-invasive, zero edits to the live custom-cl-atc.js):
 *   The existing ATC handler (assets/custom-cl-atc.js) does, on a successful /cart/add.js:
 *       document.dispatchEvent(new CustomEvent('cl:cart-added', {bubbles:true}));
 *       try { window.upcart && window.upcart.openCart && (window.upcart.openCart(), opened=true); } catch(e){}
 *       if (!opened) window.location.href = '/cart';
 *   So we do TWO things:
 *     1. Listen for 'cl:cart-added'  -> re-render + open the drawer. (primary, robust)
 *     2. Define window.upcart = { openCart }  -> makes `opened` true so the ATC handler does NOT
 *        navigate to /cart. (suppresses the fallback; must load before an add fires.)
 *   Because UpCart is installed as an app-embed, WIRING.md instructs disabling that embed so it
 *   can't clobber our shim. We guard with a getter so a late UpCart load can't silently win.
 *
 * 2026-08-29 (Viktor cart spec): the 3-card cross-sell is gone. The drawer renders ONE
 * upgrade offer from the [data-cl-config] JSON the section embeds:
 *   - bundle in cart at qty 1 -> "second person" box; CTA = qty 2 (arms the qty≥2 −10%
 *     auto discount) then straight to /checkout.
 *   - singles only            -> upgrade-to-bundle box (dose-matched, sub-matched); CTA adds
 *     the bundle, removes the singles it replaces, then straight to /checkout.
 * Plan/pack live ONLY in the Heights-style chip; line items show dose only. Line savings are
 * anchored on variant compare_at (MSRP) or the plan's compare_at, shown strike + green pill,
 * and the totals "Спестяваш" row is the sum of those line saves.
 *
 * TRAPS designed around (see ~/.claude/skills/shopify-traps/TRAPS.md):
 *   - inert-hits-own-modal (L451): background-inert helper filters `panel.contains(el)` before applying.
 *   - Sections API Accept-header hijack (L302): we DON'T use the Sections API; we read /cart.js JSON
 *     and re-render client-side, so the hijack can't bite. (add/change also use *.js, not ?sections=.)
 *   - section:unload teardown (L445): every listener registered via on() pushes its remover into
 *     teardown[]; all run on shopify:section:unload scoped to this section.
 *   - IO-not-for-sticky (L472/517): footer is a plain flex child, no IntersectionObserver, no sticky.
 */
(function () {
  'use strict';
  if (window.__clCartDrawer) return;
  window.__clCartDrawer = true;

  var SEL_ROOT = '#CLCartDrawer';

  /* Locale root: '/' on bg, '/en/' on the EN subfolder. Shopify injects Shopify.routes.root
     through content_for_header, so it is present on every storefront page. Root-relative
     literals ('/checkout', '/cart.js') do NOT carry the locale prefix: navigation would bounce
     EN shoppers onto the Bulgarian checkout, and the Ajax reads would come back with Bulgarian
     product titles inside the EN drawer. Always returns a value ending in '/'; falls back to '/'
     (today's behaviour) if Shopify or routes is undefined. */
  function clRoot() {
    var r;
    try {
      r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    } catch (x) { r = null; }
    if (typeof r !== 'string' || !r) r = '/';
    if (r.charAt(r.length - 1) !== '/') r += '/';
    return r;
  }

  var teardown = [];
  var root, panel, scrim, live, lastFocus = null;
  var CFG = null; // parsed [data-cl-config]
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- tiny helpers ---- */
  function $(s, c) { return (c || document).querySelector(s); }
  function $all(s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); }
  function on(target, ev, fn, opts) {
    if (!target) return;
    target.addEventListener(ev, fn, opts);
    teardown.push(function () { target.removeEventListener(ev, fn, opts); });
  }
  function money(cents) {
    // The page's language format via assets/custom-cl-money.js (Bulgarian unchanged: €105,00; English €105.00).
    if (window.clMoney) return window.clMoney(cents);
    var n = (Number(cents) || 0) / 100;
    return '€' + n.toFixed(2).replace('.', ',');
  }
  /* the free-shipping threshold is set in euros; a shopper paying in local currency gets it converted (custom-cl-money.js) */
  function cdFromEur(c) { return window.clFromEur ? window.clFromEur(c) : c; }
  function announce(msg) { if (live) { live.textContent = ''; live.textContent = msg; } }

  /* Update the site header's cart-count bubble (sections/header.liquid #cart-icon-bubble .cart-count-bubble)
     so it stays truthful after an AJAX add/change — we never navigate, so the server count would go stale.
     Handles the empty->non-empty case where header.liquid renders NO bubble node yet (creates one). */
  function syncCartBubble(count) {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    var bubble = icon.querySelector('.cart-count-bubble');
    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        var vis = document.createElement('span');
        vis.setAttribute('aria-hidden', 'true');
        bubble.appendChild(vis);
        icon.appendChild(bubble);
      }
      var numSpan = bubble.querySelector('span[aria-hidden="true"]');
      if (numSpan) numSpan.textContent = count < 100 ? count : '';
    } else if (bubble) {
      bubble.remove();
    }
  }

  /* ---- focus trap ---- */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';
  function trapTab(e) {
    if (e.key !== 'Tab' || !isOpen()) return;
    var f = $all(FOCUSABLE, panel).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---- background inert (filtered so it never hits the drawer's own nodes) ---- */
  var inerted = [];
  function setBackgroundInert(state) {
    if (state) {
      $all('body > *').forEach(function (el) {
        if (el === root) return;
        if (root && root.contains(el)) return;        // never inert our own tree (trap L451)
        if (el.hasAttribute('aria-hidden')) return;
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('data-cl-inerted', '');
        inerted.push(el);
      });
    } else {
      inerted.forEach(function (el) { el.removeAttribute('aria-hidden'); el.removeAttribute('data-cl-inerted'); });
      inerted = [];
    }
  }

  /* ---- scroll lock (preserve position) ---- */
  var scrollY = 0;
  /* Only lockScroll(true) ever records a scroll position, so an unconditional unlock restores
     the seed value 0 — i.e. teleports the page to the top. shopify:section:unload calls the
     unlock on every editor edit, drawer opened or not, which is exactly that case. Track
     whether we actually locked and make the unlock a no-op otherwise. */
  var scrollLocked = false;
  function lockScroll(state) {
    var b = document.body;
    if (state) {
      scrollY = window.scrollY || window.pageYOffset;
      scrollLocked = true;
      b.style.position = 'fixed'; b.style.top = (-scrollY) + 'px';
      b.style.left = '0'; b.style.right = '0'; b.style.width = '100%';
    } else {
      if (!scrollLocked) return;
      scrollLocked = false;
      b.style.position = ''; b.style.top = ''; b.style.left = ''; b.style.right = ''; b.style.width = '';
      window.scrollTo(0, scrollY);
    }
  }

  /* ---- open / close ---- */
  function isOpen() { return root && root.classList.contains('is-open'); }
  // Hide the empty-state block WITHOUT committing to "full" — used on open while /cart.js is in
  // flight so the stale server-baked empty state can't flash before render() decides the truth.
  function suppressEmptyFlash() {
    var emptyEl = $('[data-cl-empty]', root);
    if (emptyEl) emptyEl.hidden = true;
  }
  function openDrawer(opts) {
    if (!root) return;
    var alreadyOpen = isOpen();
    // Suppress a stale empty-flash: unless we're skipping the fetch, hide the empty block up front.
    // render() (post /cart.js) is the ONLY authority that may re-show it. This kills the ~1s
    // "empty drawer even though a product was just added" window on the fresh-ATC open path.
    if (!opts || !opts.skipFetch) suppressEmptyFlash();
    if (!alreadyOpen) {
      lastFocus = document.activeElement;
      root.hidden = false;
      // force reflow so the transform transition runs
      // eslint-disable-next-line no-unused-expressions
      root.offsetHeight;
      root.classList.add('is-open');
      setBackgroundInert(true);
      lockScroll(true);
      panel.setAttribute('tabindex', '-1');
      panel.focus({ preventScroll: true });
      announce((root.getAttribute('data-open-msg')) || 'Cart opened');
    }
    if (!opts || !opts.skipFetch) refresh();
  }
  function closeDrawer() {
    if (!root || !isOpen()) return;
    root.classList.remove('is-open');
    setBackgroundInert(false);
    lockScroll(false);
    var done = function () {
      if (!isOpen()) root.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    if (reduceMotion) { done(); }
    else { panel.addEventListener('transitionend', done); }
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---- server round-trips ---- */
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) return r.json().then(function (j) { throw j; }); return r.json(); });
  }
  function getCart() {
    return fetch(clRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) {
      // A 430/503 challenge page or an app-proxy interception answers 200-with-HTML or a
      // non-2xx: r.json() would reject with a parse error the callers can't tell apart from a
      // network drop. Fail loudly here so every caller's .catch is the single recovery path.
      if (!r.ok) throw new Error('cart http ' + r.status);
      return r.json();
    }).then(function (cart) { lastCart = cart; return cart; });
  }

  /* ---- savings model (mirrors the section's Liquid exactly) ----
     Per-unit strike = max(variant compare_at [MSRP, from config], plan compare_at [one-time price,
     from /cart.js]); line was = max(strike × qty, original_line_price); save = was − final. */
  function cmpFor(item) {
    var cmp = 0;
    if (CFG && CFG.cmpById && CFG.cmpById[item.variant_id]) cmp = CFG.cmpById[item.variant_id];
    var alloc = item.selling_plan_allocation;
    if (alloc && alloc.compare_at_price && alloc.compare_at_price > cmp) cmp = alloc.compare_at_price;
    return cmp;
  }
  function lineSave(item) {
    var was = cmpFor(item) * item.quantity;
    if (was < item.original_line_price) was = item.original_line_price;
    var save = was - item.final_line_price;
    return { was: was, save: save > 0 ? save : 0 };
  }

  /* ---- SKU keys (P0 spike S4) — the locale-stable identity for dose + pack ----
     singles CL-MF-<dose>, bundle CL-BND-<dose>-<3M|6M>. Segment equality only: a
     `contains '1000'` test would also match CL-BND-10000-*. A line whose SKU ends in a
     pack segment carries the pack as its LAST option (the SKU orders dose, pack the same
     way the options do), so the pack option is found by index, never by option name. */
  function skuSeg(sku, i) { return String(sku || '').split('-')[i] || ''; }
  function packCodeOf(sku) {
    var p = String(sku || '').split('-');
    var last = p[p.length - 1];
    return (last === '3M' || last === '6M') ? last : '';
  }
  function packIndexOf(item) {
    if (!packCodeOf(item.sku)) return -1;
    return (item.options_with_values || []).length - 1;
  }

  /* ---- render from /cart.js JSON ---- */
  function pluralCount(n) {
    var one = root.getAttribute('data-count-1') || 'item';
    var many = root.getAttribute('data-count-n') || 'items';
    return n + ' ' + (n === 1 ? one : many);
  }
  function render(cart) {
    if (!root) return;
    lastCart = cart;
    var empty = cart.item_count === 0;
    root.setAttribute('data-empty', empty ? 'true' : 'false');

    // header count (inside the drawer)
    var countEl = $('[data-cl-count]', root);
    if (countEl) countEl.textContent = pluralCount(cart.item_count);

    // keep the site header's cart-count bubble truthful after AJAX add/change
    // (we don't navigate, so header.liquid's server-rendered count would otherwise go stale).
    syncCartBubble(cart.item_count);

    // toggle empty vs content
    var emptyEl = $('[data-cl-empty]', root);
    var itemsEl = $('[data-cl-items]', root);
    var tagsEl = $('[data-cl-tags]', root);
    var offerEl = $('[data-cl-offer]', root);
    var footerEl = $('[data-cl-footer]', root);
    if (emptyEl) emptyEl.hidden = !empty;
    [itemsEl, tagsEl, offerEl, footerEl].forEach(function (el) { if (el) el.hidden = empty; });
    if (empty) return;

    // line items
    if (itemsEl) {
      itemsEl.innerHTML = cart.items.map(lineHTML).join('');
    }

    // chips — main pill: [пакет/каденс] | Абонамент|Еднократна покупка; discount = its own
    // deal pill (a merged Bulgarian pill overflows 375px — the row flex-wraps instead)
    if (tagsEl) {
      var chips = '<span class="cl-chiptag" data-cl-chip><span class="cl-chiptag__dot"></span>' + esc(chipText(cart)) + '</span>';
      if (cart.total_discount > 0) {
        chips += '<span class="cl-chiptag cl-chiptag--deal" data-cl-chip-deal><span class="cl-chiptag__dot"></span>' + esc(root.getAttribute('data-bundle-label') || 'Bundle discount') + '</span>';
      }
      tagsEl.innerHTML = chips;
    }

    // totals
    var subEl = $('[data-cl-subtotal]', root);
    if (subEl) subEl.textContent = money(cart.items_subtotal_price);

    // The shipping row has to track the same threshold as the progress bar — it used to be a
    // hardcoded 'Безплатна' and contradicted the bar on any cart under the threshold.
    var shipEl = $('[data-cl-ship-val]', root);
    if (shipEl) {
      var shipThreshold = cdFromEur(parseInt(root.getAttribute('data-freeship-threshold'), 10) || 10000);
      var isFree = cart.items_subtotal_price >= shipThreshold;
      shipEl.textContent = isFree
        ? (root.getAttribute('data-ship-free-word') || 'Free')
        : (root.getAttribute('data-ship-calc-word') || 'Calculated at checkout');
      shipEl.setAttribute('data-free', isFree ? '1' : '0');
      shipEl.classList.toggle('cl-totals__free', isFree);
    }
    var totalSave = cart.items.reduce(function (sum, i) { return sum + lineSave(i).save; }, 0);
    var saveRow = $('[data-cl-savings-row]', root);
    if (saveRow) {
      if (totalSave > 0) {
        saveRow.hidden = false;
        var sv = $('[data-cl-total-savings]', saveRow);
        if (sv) sv.textContent = '−' + money(totalSave);
      } else { saveRow.hidden = true; }
    }

    // free-ship progress
    var fs = $('[data-cl-freeship]', root);
    if (fs) {
      var threshold = cdFromEur(parseInt(root.getAttribute('data-freeship-threshold'), 10) || 10000);
      var remaining = threshold - cart.items_subtotal_price;
      var pct = Math.min(100, Math.round(cart.items_subtotal_price / threshold * 100));
      var label = $('[data-cl-freeship-label]', fs);
      var fill = $('[data-cl-freeship-fill]', fs);
      if (fill) fill.style.width = pct + '%';
      if (label) {
        if (remaining <= 0) {
          label.textContent = root.getAttribute('data-freeship-reached') || "You've got free shipping 🎉";
        } else {
          var tmpl = root.getAttribute('data-freeship-remaining') || '[amount] away from free shipping';
          label.textContent = tmpl.replace('[amount]', money(remaining));
        }
      }
    }

    // first-subscription hint: only while a bundle SUBSCRIPTION line is in the cart
    var fsHint = $('[data-cl-firstsub]', root);
    if (fsHint) {
      fsHint.hidden = !cart.items.some(function (i) {
        return CFG && i.product_id === CFG.bundleProductId && i.selling_plan_allocation;
      });
    }

    // the single upgrade offer
    if (offerEl) offerEl.innerHTML = offerHTML(cart);
  }

  function chipText(cart) {
    var pack = '';
    var anyPlan = cart.items.some(function (i) { return i.selling_plan_allocation; });
    for (var i = 0; i < cart.items.length && !pack; i++) {
      var ows = cart.items[i].options_with_values || [];
      var pi = packIndexOf(cart.items[i]);
      for (var j = 0; j < ows.length; j++) {
        if (j === pi && ows[j].value && ows[j].value !== 'Default Title') { pack = ows[j].value; break; }
      }
    }
    if (!pack && anyPlan) {
      var planned = cart.items.filter(function (i) { return i.selling_plan_allocation; })[0];
      if (planned) pack = planned.selling_plan_allocation.selling_plan.name;
    }
    var segs = [];
    if (pack) segs.push(pack);
    segs.push(anyPlan
      ? (root.getAttribute('data-plan-label') || 'Subscription')
      : (root.getAttribute('data-onetime-label') || 'One-time purchase'));
    return segs.join(' | ');
  }

  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; }); }
  function img(url, w) { return url ? url.replace(/(\.[a-z]+)(\?|$)/i, '_' + w + 'x$1$2') : ''; }

  function lineHTML(item) {
    var ls = lineSave(item);
    var saveWord = root.getAttribute('data-save-word') || 'You save';
    var priceRow = ls.save > 0
      ? '<s class="cl-line__was">' + money(ls.was) + '</s>' +
        '<span class="cl-line__now">' + money(item.final_line_price) + '</span>'
      : '<span class="cl-line__now">' + money(item.final_line_price) + '</span>';
    var savePill = ls.save > 0
      ? '<span class="cl-chip cl-chip--save">' + esc(saveWord) + ' ' + money(ls.save) + '</span>'
      : '';

    // dose only — the pack option lives in the chip, never repeated here
    var packIdx = packIndexOf(item);
    var shown = (item.options_with_values || [])
      .filter(function (o, idx) { return idx !== packIdx && o.value && o.value !== 'Default Title'; })
      .map(function (o) { return o.value; }).join(' / ');
    var variantLine = shown ? '<p class="cl-line__variant">' + esc(shown) + '</p>' : '';
    var sub = item.product_type ? '<p class="cl-line__sub">' + esc(item.product_type) + '</p>' : '';

    // stepper: trash at qty 1
    var down = item.quantity <= 1
      ? '<button type="button" class="cl-stepper__btn" data-cl-remove aria-label="' + esc(root.getAttribute('data-remove-aria') || 'Remove') + '">' + ICON.trash + '</button>'
      : '<button type="button" class="cl-stepper__btn" data-cl-dec aria-label="' + esc(root.getAttribute('data-dec-aria') || 'Decrease') + '">' + ICON.minus + '</button>';

    return '' +
      '<li class="cl-line" data-cl-line data-key="' + esc(item.key) + '">' +
        '<a class="cl-line__media" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">' +
          (item.image ? '<img src="' + esc(img(item.featured_image ? item.featured_image.url : item.image, 194)) + '" width="97" height="108" loading="lazy" alt="' + esc(item.product_title) + '">' : '') +
        '</a>' +
        '<div class="cl-line__body">' +
          '<a class="cl-line__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
          variantLine + sub +
          '<div class="cl-line__price-row">' + priceRow + '</div>' +
        '</div>' +
        '<div class="cl-line__foot">' + savePill +
          '<div class="cl-stepper" data-cl-stepper>' +
            down +
            '<span class="cl-stepper__num" data-cl-qty>' + item.quantity + '</span>' +
            '<button type="button" class="cl-stepper__btn" data-cl-inc aria-label="' + esc(root.getAttribute('data-inc-aria') || 'Increase') + '">' + ICON.plus + '</button>' +
          '</div>' +
        '</div>' +
      '</li>';
  }

  /* ---- the single upgrade offer ---- */
  function bundleLines(cart) {
    return cart.items.filter(function (i) { return CFG && i.product_id === CFG.bundleProductId; });
  }
  function singleLines(cart) {
    return cart.items.filter(function (i) { return CFG && CFG.singleDose && Object.prototype.hasOwnProperty.call(CFG.singleDose, String(i.product_id)); });
  }
  function offerHTML(cart) {
    if (!CFG || !CFG.strings) return '';
    var S = CFG.strings;
    var saveWord = root.getAttribute('data-save-word') || 'You save';
    var b = bundleLines(cart);
    var bq = b.reduce(function (s, i) { return s + i.quantity; }, 0);

    if (bq === 1) {
      // second-person offer: +1 pack = the qty≥2 −10% auto discount (preview math; /cart.js is
      // the truth after the click, same convention as the PDP in-card note). Grüns anatomy:
      // zone headline + reason line, then the flat product card. Image = the cart line's own.
      // Unit BEFORE code discounts: START30 is a one-set first-order code that checkout drops at qty 2,
      // so final_price (158,10) doubled promised 284,58 while checkout charged 338,58. Plan price on
      // subscriptions, list price on one-time — the −10% at qty≥2 is the only discount that survives.
      var unit = b[0].selling_plan_allocation ? b[0].selling_plan_allocation.price : b[0].original_price;
      var was2 = unit * 2;
      var now2 = Math.round(was2 * 0.9);
      var aImg = b[0].featured_image ? b[0].featured_image.url : b[0].image;
      return '' +
        '<div class="cl-offer">' +
          '<p class="cl-offer__zonehead">' + esc(S.secondTitle) + '</p>' +
          '<p class="cl-offer__zonesub">' + esc(S.secondBody) + '</p>' +
          '<article class="cl-offer__card" data-cl-offer-card>' +
            '<div class="cl-offer__media">' + (aImg ? '<img src="' + esc(img(aImg, 176)) + '" width="88" height="98" loading="lazy" alt="' + esc(CFG.bundleTitle || '') + '">' : '') + '</div>' +
            '<div class="cl-offer__body">' +
              '<p class="cl-offer__name">' + esc(S.secondName) + '</p>' +
              '<p class="cl-offer__spec">' + esc(S.secondSpec || '') + '</p>' +
              '<div class="cl-offer__price">' +
                '<s class="cl-line__was">' + money(was2) + '</s>' +
                '<span class="cl-offer__now">' + money(now2) + '</span>' +
                '<span class="cl-chip cl-chip--save">' + esc(saveWord) + ' ' + money(was2 - now2) + '</span>' +
              '</div>' +
              '<button type="button" class="cl-btn cl-btn--navy cl-offer__cta" data-cl-offer-second data-key="' + esc(b[0].key) + '" data-qty="' + (b[0].quantity + 1) + '">' + esc(S.ctaLabel) + '</button>' +
            '</div>' +
          '</article>' +
        '</div>';
    }

    if (bq === 0 && singleLines(cart).length) {
      // upgrade-to-bundle offer: dose-matched (last LMF line wins, mirrors the Liquid), sub-matched
      var dose = '1000';
      singleLines(cart).forEach(function (i) {
        var d = CFG.singleDose[String(i.product_id)];
        if (d) dose = d;
      });
      var anyPlan = cart.items.some(function (i) { return i.selling_plan_allocation; });
      var v = (CFG.bundleVariants || []).filter(function (x) { return skuSeg(x.sku, 2) === dose && packCodeOf(x.sku) === '3M'; })[0] || (CFG.bundleVariants || [])[0];
      if (!v) return '';
      var price = anyPlan && v.planId ? v.subPrice : v.price;
      var save = (v.cmp || 0) - price;
      var bImg = v.img || CFG.bundleImage;
      return '' +
        '<div class="cl-offer">' +
          '<p class="cl-offer__zonehead">' + esc(S.bundleHead) + '</p>' +
          '<p class="cl-offer__zonesub">' + esc(S.bundleSub) + '</p>' +
          '<article class="cl-offer__card" data-cl-offer-card>' +
            '<div class="cl-offer__media">' + (bImg ? '<img src="' + esc(bImg) + '" width="88" height="98" loading="lazy" alt="' + esc(CFG.bundleTitle || '') + '">' : '') + '</div>' +
            '<div class="cl-offer__body">' +
              '<p class="cl-offer__name">' + esc(CFG.bundleTitle || '') + '</p>' +
              '<p class="cl-offer__spec">' + esc(S.bundleSpec) + '</p>' +
              '<p class="cl-offer__benefit">' + esc(S.bundleBenefit) + '</p>' +
              '<div class="cl-offer__price">' +
                (v.cmp > price ? '<s class="cl-line__was">' + money(v.cmp) + '</s>' : '') +
                '<span class="cl-offer__now">' + money(price) + '</span>' +
                (save > 0 ? '<span class="cl-chip cl-chip--save">' + esc(saveWord) + ' ' + money(save) + '</span>' : '') +
              '</div>' +
              '<button type="button" class="cl-btn cl-btn--navy cl-offer__cta" data-cl-offer-bundle data-variant-id="' + v.id + '" data-plan-id="' + (anyPlan && v.planId ? v.planId : '') + '">' + esc(S.ctaLabel) + '</button>' +
            '</div>' +
          '</article>' +
          '<p class="cl-offer__note">' + esc(S.bundleNote) + '</p>' +
        '</div>';
    }

    return '';
  }

  // Inline icons (mirror snippet, so JS-rendered lines match server-rendered ones)
  var ICON = {
    trash: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M10 4h4M9 7v11m6-11v11M6 7l1 12a2 2 0 002 2h6a2 2 0 002-2l1-12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    minus: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    plus: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    check: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  /* ---- mutation serialization ----
     Every mutation posts an ABSOLUTE quantity read off the (optimistically updated) DOM, so two
     POSTs in flight at once can settle out of order and repaint the whole drawer — line price,
     subtotal, savings row, free-ship bar, upgrade offer — from the OLDER payload while the server
     holds the newer one. Nothing refetches afterwards, so the wrong number stands.
     Two guarantees fix it:
       1. one promise chain — a mutation's request is not sent until the previous one has settled,
          so the server applies them in tap order and the last write wins;
       2. a monotonic ticket — a response may only paint if no newer mutation has been issued
          since, so a burst of taps coalesces into a single final paint of the final state.
     Reads (refresh) deliberately stay OUT of the chain: enqueueing one from inside a queued
     task's .catch would wait on the chain that is waiting on it. They take a ticket too, so a
     read in flight can never paint over a mutation issued after it. */
  var mutQueue = Promise.resolve();
  var mutSeq = 0;
  function enqueue(fn) {
    var run = function () { return fn(); };
    // .catch keeps the chain alive (and never-rejecting) whatever one task does.
    mutQueue = mutQueue.then(run, run).catch(function () {});
    return mutQueue;
  }
  function isNewest(ticket) { return ticket === mutSeq; }

  /* Line-item keys are NOT stable: the qty>=2 automatic-discount reallocation rotates the key
     ("<variantId>:<hash>"), so a queued second tap that reuses the key captured at click time
     POSTs a stale id and gets a 400 — the tap is swallowed (reproduced 4/4 in Level-B runE).
     lastCart is the most recent cart JSON from ANY response — the previous queued mutation's
     response is exactly the one that rotated the key — so re-resolving against it at execution
     time costs no extra request. Identity = variant_id + selling plan id (two lines can share a
     variant when one is a subscription). */
  var lastCart = null;
  function lineIdentity(key) {
    var vid = parseInt(key, 10) || 0, plan = null;
    if (lastCart) {
      for (var i = 0; i < lastCart.items.length; i++) {
        var it = lastCart.items[i];
        if (it.key === key) {
          vid = it.variant_id;
          plan = it.selling_plan_allocation ? it.selling_plan_allocation.selling_plan.id : null;
          break;
        }
      }
    }
    return { vid: vid, plan: plan };
  }
  function resolveKey(key, ident) {
    if (!lastCart) return key;
    var items = lastCart.items, i, it;
    for (i = 0; i < items.length; i++) { if (items[i].key === key) return key; }
    for (i = 0; i < items.length; i++) {
      it = items[i];
      var itPlan = it.selling_plan_allocation ? it.selling_plan_allocation.selling_plan.id : null;
      if (it.variant_id === ident.vid && itPlan === ident.plan) return it.key;
    }
    return key;
  }

  /* ---- refresh (fetch + render) ---- */
  var refreshing = false;
  function refresh() {
    if (refreshing) return Promise.resolve();
    refreshing = true;
    var ticket = mutSeq;
    return getCart().then(function (cart) { if (isNewest(ticket)) render(cart); }).catch(function () {})
      .then(function () { refreshing = false; });
  }

  /* ---- mutations ---- */
  function changeQty(key, qty, lineEl) {
    var ticket = ++mutSeq;
    var ident = lineIdentity(key);
    if (lineEl) lineEl.classList.add('is-pending');
    return enqueue(function () {
      return postJSON(clRoot() + 'cart/change.js', { id: resolveKey(key, ident), quantity: qty })
        .then(function (cart) {
          lastCart = cart;
          // A newer tap is already queued — its response is the truth; skip this paint.
          // (is-pending goes with the node: the newer render replaces every line's markup.)
          if (!isNewest(ticket)) return;
          render(cart);
          var t = qty === 0 ? (root.getAttribute('data-removed-msg') || 'Item removed') : (root.getAttribute('data-updated-msg') || 'Cart updated');
          announce(t);
        })
        .catch(function () {
          if (lineEl) lineEl.classList.remove('is-pending');
          if (isNewest(ticket)) refresh();
        });
    });
  }

  // Second-person offer: bump the bundle line, then straight to checkout (Viktor 2026-08-29).
  // Queued like any other mutation, so the navigation happens only once this change has settled
  // behind whatever was already in flight — the shopper can't land on checkout mid-write.
  function goSecond(key, qty, cardEl) {
    var ticket = ++mutSeq;
    var ident = lineIdentity(key);
    if (cardEl) cardEl.classList.add('is-pending');
    return enqueue(function () {
      return postJSON(clRoot() + 'cart/change.js', { id: resolveKey(key, ident), quantity: qty })
        .then(function () { window.location.href = clRoot() + 'checkout'; })
        .catch(function () {
          if (cardEl) cardEl.classList.remove('is-pending');
          if (isNewest(ticket)) refresh();
        });
    });
  }

  // Bundle upgrade: add the bundle (sub-matched), remove the singles it replaces, checkout.
  function goBundle(variantId, planId, cardEl) {
    var ticket = ++mutSeq;
    if (cardEl) cardEl.classList.add('is-pending');
    var payload = { id: parseInt(variantId, 10), quantity: 1 };
    if (planId) payload.selling_plan = parseInt(planId, 10);
    return enqueue(function () {
      return postJSON(clRoot() + 'cart/add.js', payload)
        .then(function () { return getCart(); })
        .then(function (cart) {
          var updates = {};
          singleLines(cart).forEach(function (i) { updates[i.key] = 0; });
          if (Object.keys(updates).length) return postJSON(clRoot() + 'cart/update.js', { updates: updates });
        })
        .then(function () { window.location.href = clRoot() + 'checkout'; })
        .catch(function () {
          if (cardEl) cardEl.classList.remove('is-pending');
          if (isNewest(ticket)) refresh();
        });
    });
  }

  /* ---- delegated click handling inside the drawer ---- */
  function onPanelClick(e) {
    var t = e.target;
    var incBtn = t.closest && t.closest('[data-cl-inc]');
    var decBtn = t.closest && t.closest('[data-cl-dec]');
    var rmBtn = t.closest && t.closest('[data-cl-remove]');
    var offerSecond = t.closest && t.closest('[data-cl-offer-second]');
    var offerBundle = t.closest && t.closest('[data-cl-offer-bundle]');
    var closeBtn = t.closest && t.closest('[data-cl-close]');
    var checkout = t.closest && t.closest('[data-cl-checkout]');

    if (closeBtn) { e.preventDefault(); closeDrawer(); return; }
    if (checkout) { e.preventDefault(); window.location.href = clRoot() + 'checkout'; return; }

    // Click-outside close (belt-and-braces): any click that lands inside the drawer root but
    // OUTSIDE the panel is an outside/scrim click -> close. The scrim's own listener handles
    // the normal case; this also covers the bare-root hit if Dawn's div:empty rule ever
    // collapses the scrim again (see the display:block guard in the section CSS).
    if (panel && !panel.contains(t)) { e.preventDefault(); closeDrawer(); return; }

    if (offerSecond) {
      e.preventDefault();
      var key = offerSecond.getAttribute('data-key');
      var qty = parseInt(offerSecond.getAttribute('data-qty'), 10) || 2;
      // server-rendered card has no data-key baked in — resolve it live if missing
      if (key) { goSecond(key, qty, offerSecond.closest('[data-cl-offer-card]')); }
      else {
        getCart().then(function (cart) {
          var b = bundleLines(cart)[0];
          if (b) goSecond(b.key, b.quantity + 1, offerSecond.closest('[data-cl-offer-card]'));
        }).catch(function () {
          // /cart.js unreachable: recover silently to the idle CTA (never leave a stranded
          // pending state on the card) and resync the drawer. The button stays tappable.
          var card = offerSecond.closest('[data-cl-offer-card]');
          if (card) card.classList.remove('is-pending');
          refresh();
        });
      }
      return;
    }
    if (offerBundle) {
      e.preventDefault();
      goBundle(offerBundle.getAttribute('data-variant-id'), offerBundle.getAttribute('data-plan-id'), offerBundle.closest('[data-cl-offer-card]'));
      return;
    }

    var line = t.closest && t.closest('[data-cl-line]');
    if (incBtn && line) {
      e.preventDefault();
      var q = parseInt($('[data-cl-qty]', line).textContent, 10) || 1;
      // optimistic
      $('[data-cl-qty]', line).textContent = q + 1;
      changeQty(line.getAttribute('data-key'), q + 1, line);
      return;
    }
    if (decBtn && line) {
      e.preventDefault();
      var qd = parseInt($('[data-cl-qty]', line).textContent, 10) || 1;
      $('[data-cl-qty]', line).textContent = Math.max(0, qd - 1);
      changeQty(line.getAttribute('data-key'), qd - 1, line);
      return;
    }
    if (rmBtn && line) {
      e.preventDefault();
      changeQty(line.getAttribute('data-key'), 0, line);
      return;
    }
  }

  /* ---- cart-icon interception (progressive enhancement) ---- */
  function onDocClick(e) {
    var trigger = e.target.closest && e.target.closest('#cart-icon-bubble, a[href$="/cart"], [data-cl-cart-open]');
    if (!trigger) return;
    // let modified clicks / new-tab through to native /cart
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    openDrawer();
  }

  /* ---- boot ---- */
  function boot() {
    root = $(SEL_ROOT);
    if (!root) return;
    panel = $('.cl-drawer__panel', root);
    scrim = $('[data-cl-scrim]', root);
    live = $('[data-cl-live]', root);

    // offer/catalog config (chip option name, bundle variants + compare-ats, offer strings)
    CFG = null;
    try {
      var cfgEl = $('[data-cl-config]', root);
      if (cfgEl) CFG = JSON.parse(cfgEl.textContent);
      if (CFG && CFG.bundleVariants) {
        CFG.cmpById = {};
        CFG.bundleVariants.forEach(function (v) { if (v.cmp) CFG.cmpById[v.id] = v.cmp; });
      }
    } catch (err) { CFG = null; }

    on(document, 'click', onDocClick);
    on(root, 'click', onPanelClick);
    on(scrim, 'click', closeDrawer);
    on(document, 'keydown', function (e) { if (e.key === 'Escape' && isOpen()) closeDrawer(); });
    on(document, 'keydown', trapTab);

    // Hook 1: the existing ATC handler's success event
    on(document, 'cl:cart-added', function () { openDrawer(); });

    // Hook 2: shim window.upcart so custom-cl-atc.js does NOT navigate to /cart.
    // Guard with a getter so a late UpCart bundle can't silently replace us.
    try {
      if (!window.upcart || !window.upcart.__clShim) {
        Object.defineProperty(window, 'upcart', {
          configurable: true,
          get: function () { return { __clShim: true, openCart: function () { openDrawer(); } }; },
          set: function () { /* ignore late UpCart assignment; disable its embed instead (WIRING) */ }
        });
      }
    } catch (err) {
      window.upcart = { __clShim: true, openCart: function () { openDrawer(); } };
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme-editor teardown (trap L445): remove all listeners when this section unloads/reloads.
  document.addEventListener('shopify:section:unload', function (e) {
    if (!root || !e.target.contains(root)) return;
    teardown.forEach(function (fn) { try { fn(); } catch (x) {} });
    teardown = [];
    setBackgroundInert(false);
    lockScroll(false);
    window.__clCartDrawer = false;
  });
  // Re-init when re-added in the editor
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector && e.target.querySelector(SEL_ROOT)) { window.__clCartDrawer = true; boot(); }
  });
})();
